import { create } from 'zustand';
import { supabase } from '@/lib/supabaseClient';
import type { ProductAttribute } from '@/shared/types';
import type { FilterAstNode } from '@truss-security/truss-sdk-internal';
import {
  addComparisonClause,
  astToFilterExpression,
  equalsIgnoreCase,
  hasComparisonClause,
  parseExpressionToAst,
  pruneAst,
} from '@truss-security/truss-sdk-internal';

export interface Filter {
  id: string;
  user_id: string;
  name: string;
  description?: string | null;
  filter_config: {
    filterExpression?: string;
    filterAst?: FilterAstNode | null;
    dateRange: {
      mode?: 'relative' | 'absolute';
      // Relative date range properties
      value?: number;
      unit?: 'hours' | 'days';
      label?: string;
      computed?: {
        from: string;
        to: string;
      };
      // Absolute date range properties (also used for legacy format)
      from?: string;
      to?: string;
    };
  };
  integrations?: {
    integration_config: {
      integration_type: string;
      integration_name: string;
    };
    run_interval_minutes: number;
  }[];
  created_at: string;
  updated_at: string;
}

export type FilterStatus = 'new' | 'saved' | 'unsaved';
interface FilterStore {
  currentFilter: Filter;
  filterExpression: string | null;
  filterAst: FilterAstNode | null;
  filterAstParseError: string | null;
  isFilterProjectionLossy: boolean;
  currentFilterStatus: FilterStatus;
  savedFilters: Filter[];
  isLoading: boolean;
  error: string | null;
  currentUserId: string | null;
  filterChangeCounter: number; // Increments when AST/expression actually changes
  lastFilterChange: number; // Timestamp
  fetchSavedFilters: () => Promise<void>;
  setCurrentFilter: (filter: Filter) => void;
  clearFilter: () => void;
  loadSavedFilter: (filter: Filter) => void;
  saveFilter: (name: string, description: string | null, filterConfig: Filter['filter_config']) => Promise<void>;
  updateSavedFilter: (id: string, updates: Partial<Filter>) => Promise<void>;
  deleteSavedFilter: (id: string) => Promise<void>;
  updateActiveFilterMetadata: (updates: Partial<Filter>) => void;
  verifyOwnershipOfSavedFilter: (filterId: string) => Promise<boolean>;
  setFilterAst: (ast: FilterAstNode | null) => void;
  setFilterExpression: (expression: string | null) => void;
  addFilterClause: (key: string, value: string) => void;
  removeFilterClause: (key: string, value: string) => void;
  removeFilterAttribute: (key: string) => void;
}
const initialFilter: Filter = {
  id: '',
  user_id: '',
  name: '',
  description: '',
  filter_config: {
    filterExpression: null,
    filterAst: null,
    dateRange: {
      from: '',
      to: ''
    }
  },
  created_at: new Date().toISOString(),
  updated_at: new Date().toISOString()
};

const DEBUG_FILTER_TRANSLATION = (import.meta as any)?.env?.VITE_DEBUG_FILTER_TRANSLATION === 'true';

const filterAstChanged = (
  prev: FilterAstNode | null | undefined,
  current: FilterAstNode | null | undefined
): boolean => {
  return JSON.stringify(prev ?? null) !== JSON.stringify(current ?? null);
};

const filterExpressionChanged = (prev: string | null | undefined, current: string | null | undefined): boolean => {
  return (prev || '') !== (current || '');
};

const filterStateChanged = (
  prevAst: FilterAstNode | null | undefined,
  currentAst: FilterAstNode | null | undefined,
  prevExpression: string | null | undefined,
  currentExpression: string | null | undefined
): boolean => {
  return (
    filterAstChanged(prevAst, currentAst) ||
    filterExpressionChanged(prevExpression, currentExpression)
  );
};

const logFilterTranslation = (
  sourceAction: string,
  payload: Record<string, unknown>
) => {
  if (!DEBUG_FILTER_TRANSLATION) return;
  console.log('[FilterStore] filterTranslation', {
    sourceAction,
    ...payload
  });
};

const normalizeFilterConfigForPersistence = (filterConfig: Filter['filter_config']): Filter['filter_config'] => ({
  filterExpression: filterConfig.filterExpression ?? null,
  filterAst: filterConfig.filterAst ?? null,
  dateRange: filterConfig.dateRange,
});

export const useFullFilterStore = create<FilterStore>((set, get) => ({
  currentFilter: initialFilter,
  filterExpression: null,
  filterAst: null,
  filterAstParseError: null,
  isFilterProjectionLossy: false,
  currentFilterStatus: 'new',
  savedFilters: [],
  isLoading: false,
  error: null,
  currentUserId: null,
  filterChangeCounter: 0,
  lastFilterChange: Date.now(),

  fetchSavedFilters: async () => {
    set({ isLoading: true });
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No user found');

      const { data, error } = await supabase
        .from('saved_filters')
        .select(`
          *,
          integrations:integration_filters(
            run_interval_minutes,
            integration_config:integration_configs(
              integration_type,
              integration_name
            )
          )
        `)
        .eq('user_id', user.id);

      if (error) throw error;
      set({ savedFilters: data || [] });
    } catch (error) {
      console.error('Error fetching filters:', error);
    } finally {
      set({ isLoading: false });
    }
  },

  verifyOwnershipOfSavedFilter: async (filterId: string) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return false;

      const { data, error } = await supabase
        .from('saved_filters')
        .select('user_id')
        .eq('id', filterId)
        .single();

      if (error || !data) return false;
      return data.user_id === user.id;
    } catch {
      return false;
    }
  },

  setCurrentFilter: (filter: Filter) => {
    const prevAst = get().filterAst;
    const prevExpression = get().filterExpression;
    const newExpression = filter.filter_config.filterExpression ?? null;
    const nextAst = filter.filter_config.filterAst ?? null;
    const nextParseError =
      !nextAst && !!newExpression
        ? 'Saved filter config is missing required filterAst'
        : null;
    const changed = filterStateChanged(prevAst, nextAst, prevExpression, newExpression);
    logFilterTranslation('setCurrentFilter', {
      filterId: filter.id,
      normalizedExpression: newExpression,
      parsedAstExpression: astToFilterExpression(nextAst),
      parseError: nextParseError
    });
    
    set({
      currentFilter: {
        ...filter,
        filter_config: {
          ...filter.filter_config,
          filterExpression: newExpression,
          filterAst: nextAst,
        }
      },
      currentFilterStatus: 'saved',
      filterExpression: newExpression,
      filterAst: nextAst,
      filterAstParseError: nextParseError,
      isFilterProjectionLossy: false,
      ...(changed ? {
        filterChangeCounter: get().filterChangeCounter + 1,
        lastFilterChange: Date.now(),
      } : {}),
    });
  },

  clearFilter: () => {
    const prevAst = get().filterAst;
    const prevExpression = get().filterExpression;
    const changed = filterStateChanged(prevAst, null, prevExpression, null);
    logFilterTranslation('clearFilter', {
      normalizedExpression: null,
      parsedAstExpression: '',
      isProjectionLossy: false,
      parseError: null
    });
    
    set({
      filterExpression: null,
      filterAst: null,
      filterAstParseError: null,
      isFilterProjectionLossy: false,
      currentFilter: initialFilter,
      currentFilterStatus: 'new',
      ...(changed ? {
        filterChangeCounter: get().filterChangeCounter + 1,
        lastFilterChange: Date.now(),
      } : {}),
    });
  },

  loadSavedFilter: (filter: Filter) => {
    const prevAst = get().filterAst;
    const prevExpression = get().filterExpression;
    const newExpression = filter.filter_config.filterExpression ?? null;
    const nextAst = filter.filter_config.filterAst ?? null;
    const nextParseError =
      !nextAst && !!newExpression
        ? 'Saved filter config is missing required filterAst'
        : null;
    const changed = filterStateChanged(prevAst, nextAst, prevExpression, newExpression);
    logFilterTranslation('loadSavedFilter', {
      filterId: filter.id,
      normalizedExpression: newExpression,
      parsedAstExpression: astToFilterExpression(nextAst),
      parseError: nextParseError
    });
    
    set({
      currentFilter: {
        ...filter,
        filter_config: {
          ...filter.filter_config,
          filterExpression: newExpression,
          filterAst: nextAst,
        }
      },
      currentFilterStatus: 'saved',
      filterExpression: newExpression,
      filterAst: nextAst,
      filterAstParseError: nextParseError,
      isFilterProjectionLossy: false,
      ...(changed ? {
        filterChangeCounter: get().filterChangeCounter + 1,
        lastFilterChange: Date.now(),
      } : {}),
    });
  },

  saveFilter: async (name, description, filterConfig) => {
    set({ isLoading: true, error: null });
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No user found');

      const { data, error } = await supabase
        .from('saved_filters')
        .insert({
          user_id: user.id,
          name,
          description: description?.trim() ? description.trim() : null,
          filter_config: normalizeFilterConfigForPersistence(filterConfig),
        })
        .select()
        .single();

      if (error) throw error;
      await get().fetchSavedFilters();
      set(() => ({
        currentFilter: data,
        currentFilterStatus: 'saved'
      }));
    } catch (error) {
      set({ error: (error as Error).message });
      throw error;
    } finally {
      set({ isLoading: false });
    }
  },

  updateSavedFilter: async (id, updates) => {
    set({ isLoading: true, error: null });
    try {
      const isOwner = await get().verifyOwnershipOfSavedFilter(id);
      if (!isOwner) throw new Error('Unauthorized to update this filter');

      const updatePayload = {
        ...updates,
        ...(updates.filter_config ? { filter_config: normalizeFilterConfigForPersistence(updates.filter_config) } : {}),
        updated_at: new Date().toISOString(),
      };

      const { data, error } = await supabase
        .from('saved_filters')
        .update(updatePayload)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      await get().fetchSavedFilters();
      set(() => ({
        currentFilter: data,
        currentFilterStatus: 'saved'
      }));
    } catch (error) {
      set({ error: (error as Error).message });
      throw error;
    } finally {
      set({ isLoading: false });
    }
  },

  deleteSavedFilter: async (id: string) => {
    set({ isLoading: true, error: null });
    try {
      const isOwner = await get().verifyOwnershipOfSavedFilter(id);
      if (!isOwner) throw new Error('Unauthorized to delete this filter');

      const { error } = await supabase
        .from('saved_filters')
        .delete()
        .eq('id', id);

      if (error) throw error;
      await get().fetchSavedFilters();
    } catch (error) {
      set({ error: (error as Error).message });
      throw error;
    } finally {
      set({ isLoading: false });
    }
  },

  updateActiveFilterMetadata: (updates) => set((state) => ({
    currentFilter: {
      ...state.currentFilter,
      ...updates
    }
  })),

  setFilterAst: (ast) => set((state) => {
    const normalizedAst = ast ?? null;
    const normalizedExpression = normalizedAst ? astToFilterExpression(normalizedAst) : null;
    const changed = filterStateChanged(state.filterAst, normalizedAst, state.filterExpression, normalizedExpression);
    logFilterTranslation('setFilterAst', {
      normalizedExpression,
      parsedAstExpression: astToFilterExpression(normalizedAst),
      parseError: null,
      changed,
    });
    return {
      filterExpression: normalizedExpression,
      filterAst: normalizedAst,
      filterAstParseError: null,
      isFilterProjectionLossy: false,
      currentFilterStatus: 'unsaved',
      currentFilter: {
        ...state.currentFilter,
        filter_config: {
          ...state.currentFilter.filter_config,
          filterExpression: normalizedExpression,
          filterAst: normalizedAst,
        }
      },
      ...(changed ? {
        filterChangeCounter: state.filterChangeCounter + 1,
        lastFilterChange: Date.now(),
      } : {}),
    };
  }),

  setFilterExpression: (expression) => set((state) => {
    const normalizedExpression = expression && expression.trim() ? expression : null;
    const parsed = parseExpressionToAst(normalizedExpression);
    const astForState = normalizedExpression ? parsed.ast : null;
    const parseError = normalizedExpression ? parsed.error : null;
    const changed = filterStateChanged(state.filterAst, astForState, state.filterExpression, normalizedExpression);
    logFilterTranslation('setFilterExpression', {
      inputExpression: expression,
      normalizedExpression,
      parsedAstExpression: astToFilterExpression(astForState),
      previousExpression: state.filterExpression,
      isProjectionLossy: false,
      parseError,
      changed
    });
    return {
      filterExpression: normalizedExpression,
      filterAst: astForState,
      filterAstParseError: parseError,
      isFilterProjectionLossy: false,
      currentFilterStatus: 'unsaved',
      currentFilter: {
        ...state.currentFilter,
        filter_config: {
          ...state.currentFilter.filter_config,
          filterExpression: normalizedExpression,
          filterAst: astForState,
        }
      },
      ...(changed ? {
        filterChangeCounter: state.filterChangeCounter + 1,
        lastFilterChange: Date.now(),
      } : {}),
    };
  }),

  addFilterClause: (key, value) => set((state) => {
    const attribute = key as ProductAttribute;
    const currentAst = state.filterAst ?? null;
    if (hasComparisonClause(currentAst, attribute, value)) {
      return state;
    }
    const added = addComparisonClause(currentAst, attribute, value);
    if (added.error) {
      return {
        ...state,
        filterAstParseError: added.error,
      };
    }
    const nextAst = added.ast;
    const nextExpression = astToFilterExpression(nextAst);
    const changed = filterStateChanged(state.filterAst, nextAst, state.filterExpression, nextExpression);
    logFilterTranslation('addFilterClause', {
      key,
      value,
      normalizedExpression: nextExpression,
      parsedAstExpression: nextExpression,
      parseError: null,
      changed,
    });
    return {
      filterExpression: nextExpression,
      filterAst: nextAst,
      filterAstParseError: null,
      isFilterProjectionLossy: false,
      currentFilterStatus: 'unsaved',
      currentFilter: {
        ...state.currentFilter,
        filter_config: {
          ...state.currentFilter.filter_config,
          filterExpression: nextExpression,
          filterAst: nextAst,
        }
      },
      ...(changed ? {
        filterChangeCounter: state.filterChangeCounter + 1,
        lastFilterChange: Date.now(),
      } : {}),
    };
  }),

  removeFilterClause: (key, value) => set((state) => {
    const attribute = key as ProductAttribute;
    const nextAst = pruneAst(state.filterAst ?? null, (node) =>
      node.attribute === attribute && node.operator === '=' && equalsIgnoreCase(node.value, value)
    );
    const nextExpression = nextAst ? astToFilterExpression(nextAst) : null;
    const changed = filterStateChanged(state.filterAst, nextAst, state.filterExpression, nextExpression);
    logFilterTranslation('removeFilterClause', {
      key,
      value,
      normalizedExpression: nextExpression,
      parsedAstExpression: nextExpression || '',
      parseError: null,
      changed,
    });
    return {
      filterExpression: nextExpression,
      filterAst: nextAst,
      filterAstParseError: null,
      isFilterProjectionLossy: false,
      currentFilterStatus: 'unsaved',
      currentFilter: {
        ...state.currentFilter,
        filter_config: {
          ...state.currentFilter.filter_config,
          filterExpression: nextExpression,
          filterAst: nextAst,
        }
      },
      ...(changed ? {
        filterChangeCounter: state.filterChangeCounter + 1,
        lastFilterChange: Date.now(),
      } : {}),
    };
  }),

  removeFilterAttribute: (key) => set((state) => {
    const attribute = key as ProductAttribute;
    const nextAst = pruneAst(state.filterAst ?? null, (node) => node.attribute === attribute);
    const nextExpression = nextAst ? astToFilterExpression(nextAst) : null;
    const changed = filterStateChanged(state.filterAst, nextAst, state.filterExpression, nextExpression);
    logFilterTranslation('removeFilterAttribute', {
      key,
      normalizedExpression: nextExpression,
      parsedAstExpression: nextExpression || '',
      parseError: null,
      changed,
    });
    return {
      filterExpression: nextExpression,
      filterAst: nextAst,
      filterAstParseError: null,
      isFilterProjectionLossy: false,
      currentFilterStatus: 'unsaved',
      currentFilter: {
        ...state.currentFilter,
        filter_config: {
          ...state.currentFilter.filter_config,
          filterExpression: nextExpression,
          filterAst: nextAst,
        }
      },
      ...(changed ? {
        filterChangeCounter: state.filterChangeCounter + 1,
        lastFilterChange: Date.now(),
      } : {}),
    };
  }),

})); 